import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiClock, FiXCircle } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PaymentService from '../../services/paymentService';

const STATUS_UI = {
  success: {
    icon: FiCheckCircle,
    color: '#22C55E',
    bg: '#F0FDF4',
    title: 'Payment Successful',
    message: 'Your subscription is now active.',
  },
  failed: {
    icon: FiXCircle,
    color: '#EF4444',
    bg: '#FEF2F2',
    title: 'Payment Failed',
    message: 'The payment could not be completed. No amount was charged.',
  },
  pending: {
    icon: FiClock,
    color: '#F59E0B',
    bg: '#FFFBEB',
    title: 'Payment Pending',
    message: 'We are still confirming this payment. It will update shortly.',
  },
  timeout: {
    icon: FiClock,
    color: '#F59E0B',
    bg: '#FFFBEB',
    title: 'Payment Pending',
    message: 'The payment timed out while confirming. Please check again shortly.',
  },
  cancelled: {
    icon: FiAlertTriangle,
    color: '#6B7280',
    bg: '#F9FAFB',
    title: 'Payment Cancelled',
    message: 'The payment was cancelled before completion.',
  },
};

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = searchParams.get('orderId');
  const [status, setStatus] = useState(searchParams.get('status') || 'pending');
  const [amount, setAmount] = useState(searchParams.get('amount') || '');
  const plan = searchParams.get('plan') || '';

  useEffect(() => {
    // Best-effort re-verification with the backend (agent is logged in here).
    if (!orderId) return;
    if (!localStorage.getItem('agent_token')) return;

    let active = true;
    (async () => {
      try {
        const res = await PaymentService.getStatus(orderId);
        if (active && res?.success && res?.data) {
          setStatus(res.data.status || status);
          if (res.data.amount != null) setAmount(String(res.data.amount));
        }
      } catch (error) {
        // Keep the status from the query params on any error.
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const ui = STATUS_UI[status] || STATUS_UI.pending;
  const Icon = ui.icon;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-400 p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center"
      >
        <div
          className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full"
          style={{ backgroundColor: ui.bg }}
        >
          <Icon style={{ color: ui.color }} className="h-11 w-11" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">{ui.title}</h1>
        <p className="text-gray-500 mb-6">{ui.message}</p>

        <div className="rounded-xl bg-gray-50 divide-y divide-gray-100 text-left mb-6">
          {plan && (
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-gray-500">Plan</span>
              <span className="text-sm font-medium text-gray-900 capitalize">{plan}</span>
            </div>
          )}
          {amount && (
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-gray-500">Amount</span>
              <span className="text-sm font-medium text-gray-900">
                ₹{Number(amount).toLocaleString('en-IN')}
              </span>
            </div>
          )}
          {orderId && (
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-gray-500">Order ID</span>
              <span className="text-xs font-mono text-gray-700">{orderId}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full gradient-bg text-white font-semibold py-3 rounded-xl shadow-md hover:opacity-95 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentResult;
